import React, { useState, useEffect, useRef } from "react";
import { 
  Box, 
  Flex, 
  Heading, 
  Text, 
  Button, 
  Badge, 
  VStack, 
  HStack,
  Spacer,
  useDisclosure,
  SlideFade,
  Spinner,
  Alert,
  AlertIcon,
  CircularProgress,
  CircularProgressLabel
} from "@chakra-ui/react";
import { PendingActionItem } from "./types";
import PendingAction from "./PendingAction";
import { useNavigate } from "react-router-dom";
import InfoModal from "../common/InfoModal";
import { ClientCard } from "../clients/ClientCard";
import { SalesExecutiveCard } from "../sales/SalesExecutiveCard";
import { clientService, ClientData } from "../../services/clientService";
import { salesPersonService, SalesPersonDetails } from "../../services/salesPersonService";
import { metricsService } from "../../services/metricsService";
import { conversationService } from "../../services/conversationService";
import salesSummaryData from "../Projects/sales_summary.json";
import { useMeetingContext } from "./MeetingContext";
import { formatDate } from "../../utils/timeUtils";
import { getUserBusinessId } from "../../utils/auth";
import { formatAudioTime, formatAudioDuration } from "./AudioRecordingPlayer";


// Define props for MeetingCard based on the updated Meeting interface from meetingService
interface MeetingCardProps {
  id: number | string;
  meeting_id: string; // Session ID - always present
  conversation_id: string | string[] | null; // Actual conversation ID - can be null or array of IDs
  name: string;
  date: string;
  project: string;
  client: string;
  clientId?: string | number;
  salesRep: string;
  salesExecutiveId: string | number;
  duration?: string;
  note?: string;
  status?: string;
  scorePercentage?: number | null;
  pendingActions?: {
    count: number;
    items: Array<PendingActionItem>;
  } | Array<any>;
}

interface AnimatedMeetingCardProps extends MeetingCardProps {
  delay?: number;
}

// Calculate the number of objections and moments of excitement for sentiment scoring
// Added meetingId parameter to ensure unique scores per meeting
const calculateSentimentScore = (projectId: string, client: string, salesRep: string, meetingId?: string) => {
  // Use meetingId as seed for consistent but unique random values per meeting
  const seed = meetingId ? meetingId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : Math.random() * 1000;
  
  // Find exact match first for project, client, and salesperson
  const exactMatch = salesSummaryData.find(item => 
    item.project_id === projectId && 
    item.sales_summary_report?.client?.toLowerCase() === client.toLowerCase() &&
    item.sales_summary_report?.salesman?.toLowerCase() === salesRep.toLowerCase()
  );
  
  if (exactMatch) {
    const objections = exactMatch.sales_summary_report?.objections_handled || [];
    const moments = exactMatch.sales_summary_report?.moments_of_excitement_handled || [];
    
    // Calculate sentiment based on objections handled and moments of excitement
    // More moments and fewer objections = higher sentiment
    const objectionCount = Array.isArray(objections) ? objections.length : 0;
    const momentCount = Array.isArray(moments) ? moments.length : 0;
    
    // Base value plus adjustments for objections and moments
    const clientSentiment = Math.min(95, Math.max(60, 75 + (momentCount * 3) - (objectionCount * 2)));
    
    // Generate related scores based on client sentiment with meeting-specific variation
    const baseAdjustment = (clientSentiment - 75) / 2; // Scale the adjustment
    const meetingVariation = (seed % 20) - 10; // -10 to +10 variation based on meeting ID
    const callQualityScore = Math.min(95, Math.max(60, 75 + baseAdjustment + meetingVariation));
    const sopAdherence = Math.min(95, Math.max(60, 75 + baseAdjustment - 5 + meetingVariation));
    const objectionHandlingScore = Math.min(95, Math.max(60, 70 + baseAdjustment + 3 + meetingVariation));
    
    return {
      clientSentiment,
      callQualityScore,
      sopAdherence,
      objectionHandlingScore,
      talkListenRatio: Math.min(80, Math.max(40, 55 + (seed % 20) - 10)),
      sopDeviationPercentage: Math.min(95, Math.max(60, 75 + (objectionCount ? -objectionCount * 2 : 0) + (seed % 10) - 5)),
      buyer_interest_level_text: undefined,
      overall_deal_sentiment_score: undefined
    };
  }
  
  // If no exact match, find any entry for this project as fallback
  const projectMatch = salesSummaryData.find(item => item.project_id === projectId);
  
  if (projectMatch) {
    const objections = projectMatch.sales_summary_report?.objections_handled || [];
    const moments = projectMatch.sales_summary_report?.moments_of_excitement_handled || [];
    
    const objectionCount = Array.isArray(objections) ? objections.length : 0;
    const momentCount = Array.isArray(moments) ? moments.length : 0;
    
    const clientSentiment = Math.min(95, Math.max(60, 70 + (momentCount * 3) - (objectionCount * 2)));
    
    const baseAdjustment = (clientSentiment - 70) / 2;
    const meetingVariation = (seed % 20) - 10;
    const callQualityScore = Math.min(95, Math.max(60, 70 + baseAdjustment + meetingVariation));
    const sopAdherence = Math.min(95, Math.max(60, 70 + baseAdjustment - 5 + meetingVariation));
    const objectionHandlingScore = Math.min(95, Math.max(60, 65 + baseAdjustment + 3 + meetingVariation));
    
    return {
      clientSentiment,
      callQualityScore,
      sopAdherence,
      objectionHandlingScore,
      talkListenRatio: Math.min(80, Math.max(40, 50 + (seed % 20) - 10)),
      sopDeviationPercentage: Math.min(95, Math.max(60, 70 + (objectionCount ? -objectionCount * 2 : 0) + (seed % 10) - 5)),
      buyer_interest_level_text: undefined,
      overall_deal_sentiment_score: undefined
    };
  }
  
  // Default if no data found at all - use meetingId for unique but consistent values
  const baseValue = 65 + (seed % 31);
  return {
    clientSentiment: baseValue,
    callQualityScore: 65 + ((seed * 2) % 31),
    sopAdherence: 65 + ((seed * 3) % 31),
    objectionHandlingScore: 65 + ((seed * 5) % 31),
    talkListenRatio: 40 + ((seed * 7) % 41),
    sopDeviationPercentage: 65 + ((seed * 11) % 31),
    buyer_interest_level_text: undefined,
    overall_deal_sentiment_score: undefined
  };
};

const MeetingCard: React.FC<AnimatedMeetingCardProps> = ({
  id,
  meeting_id,
  conversation_id,
  name,
  date,
  project,
  client,
  clientId,
  salesRep,
  salesExecutiveId,
  duration,
  pendingActions,
  delay = 0
}) => {
  const navigate = useNavigate();
  const { isOpen: isClientModalOpen, onOpen: onClientModalOpenOrig, onClose: onClientModalCloseOrig } = useDisclosure();
  const { isOpen: isRepModalOpen, onOpen: onRepModalOpenOrig, onClose: onRepModalCloseOrig } = useDisclosure();
  
  // State for real metrics from API
  const [metrics, setMetrics] = useState<{
    clientSentiment: number;
    callQualityScore: number;
    sopAdherence: number;
    sopDeviationPercentage: number;
    objectionHandlingScore: number;
    talkListenRatio: number;
    buyer_interest_level_text?: string;
    overall_deal_sentiment_score?: number;
  } | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Duration (seconds) of this meeting's audio recording, shown on the card.
  const [audioDuration, setAudioDuration] = useState<number | null>(null);

  const [clientDetails, setClientDetails] = useState<ClientData | null>(null);
  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);

  const [salesPersonDetails, setSalesPersonDetails] = useState<SalesPersonDetails | null>(null);
  const [isLoadingSalesPerson, setIsLoadingSalesPerson] = useState(false);
  const [salesPersonError, setSalesPersonError] = useState<string | null>(null);
  const [filteredMeetingData, setFilteredMeetings] = useState<any>([]);

  // Only fetch this card's metrics once it has scrolled into view. With many
  // cards on screen, each firing 5 metric requests, fetching them all on mount
  // floods the network; deferring off-screen cards keeps the page responsive.
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  useEffect(() => {
    if (hasBeenVisible) return;
    const el = cardRef.current;
    // No element or no IntersectionObserver support → just load eagerly.
    if (!el || typeof IntersectionObserver === "undefined") {
      setHasBeenVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasBeenVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" } // start fetching slightly before the card is visible
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasBeenVisible]);

  // Fetch real metrics data once the card becomes visible
  useEffect(() => {;
    // Wait until the card is on (or near) screen
    if (!hasBeenVisible) {
      return;
    }
    // Early return if we don't have required data
    if (!conversation_id || !meeting_id) {
      return;
    }
    
    // Normalize conversation_id to an array
    const conversationIds = Array.isArray(conversation_id) 
      ? conversation_id.filter(id => id && typeof id === 'string' && id.trim() !== '')
      : (conversation_id && typeof conversation_id === 'string' && conversation_id.trim() !== '')
        ? [conversation_id]
        : [];
    
    if (conversationIds.length === 0) {
      return;
    }
    
    // Only proceed when we have the essential data
    const fetchMetrics = async () => {
      setMetricsLoading(true);
      
      // Always try to fetch from API first, regardless of conversation_id
      try {
        let metrics = null;
        
        if (conversationIds.length > 0) {
          // Type for metrics result
          type MetricsResult = {
            dealSentiment: any;
            callQuality: any;
            sopDeviation: any;
            talkListenRatio: any;
            objectionHandling: any;
          };

          // Fetch metrics for all conversation IDs
          const allMetricsPromises = conversationIds.map(async (convId): Promise<MetricsResult | null> => {
            try {
              const [dealSentiment, callQuality, sopDeviation, talkListenRatio, objectionHandling] = await Promise.all([
                metricsService.getConversationDealSentiment(convId).catch((err) => {
                  console.warn(`[MeetingCard ${meeting_id}] Failed to fetch deal sentiment for conversation ${convId}:`, err);
                  return null;
                }),
                metricsService.getConversationCallQuality(convId).catch((err) => {
                  console.warn(`[MeetingCard ${meeting_id}] Failed to fetch call quality for conversation ${convId}:`, err);
                  return null;
                }),
                metricsService.getConversationSopDeviation(convId).catch((err) => {
                  console.warn(`[MeetingCard ${meeting_id}] Failed to fetch SOP deviation for conversation ${convId}:`, err);
                  return null;
                }),
                metricsService.getConversationTalkListenRatio(convId).catch((err) => {
                  console.warn(`[MeetingCard ${meeting_id}] Failed to fetch talk/listen ratio for conversation ${convId}:`, err);
                  return null;
                }),
                metricsService.getConversationObjectionHandlingScore(convId).catch((err) => {
                  console.warn(`[MeetingCard ${meeting_id}] Failed to fetch objection handling score for conversation ${convId}:`, err);
                  return null;
                })
              ]);

              return {
                dealSentiment,
                callQuality,
                sopDeviation,
                talkListenRatio,
                objectionHandling
              };
            } catch (error) {
              console.warn(`[MeetingCard ${meeting_id}] Error fetching metrics for conversation ${convId}:`, error);
              return null;
            }
          });

          const allMetricsResults = await Promise.all(allMetricsPromises);
          
          // Filter out null results and aggregate metrics with type guard
          const validMetrics = allMetricsResults.filter((m): m is MetricsResult => m !== null);
          
          if (validMetrics.length > 0) {
            // Get deal sentiment data from the first valid deal sentiment response
            const firstDealSentiment = validMetrics.find(m => m.dealSentiment)?.dealSentiment;

            // Aggregate metrics by averaging values
            let totalDealSentiment = 0;
            let totalCallQuality = 0;
            let totalSopAdherence = 0;
            let totalSopDeviationPercentage = 0;
            let totalObjectionHandling = 0;
            let totalTalkListenRatio = 0;
            
            let countDealSentiment = 0;
            let countCallQuality = 0;
            let countSopAdherence = 0;
            let countSopDeviationPercentage = 0;
            let countObjectionHandling = 0;
            let countTalkListenRatio = 0;

            validMetrics.forEach(m => {
              if (m.dealSentiment?.buyer_interest_level_score != null) {
                totalDealSentiment += m.dealSentiment.buyer_interest_level_score;
                countDealSentiment++;
              }
              if (m.callQuality?.overall_call_quality_score != null) {
                totalCallQuality += m.callQuality.overall_call_quality_score;
                countCallQuality++;
              }
              if (m.sopDeviation?.weighted_stage_score != null) {
                totalSopAdherence += m.sopDeviation.weighted_stage_score;
                countSopAdherence++;
              }
              if (m.sopDeviation?.sop_deviation_percentage != null) {
                totalSopDeviationPercentage += m.sopDeviation.sop_deviation_percentage;
                countSopDeviationPercentage++;
              }
              if (m.objectionHandling?.objection_handling_score != null) {
                totalObjectionHandling += m.objectionHandling.objection_handling_score;
                countObjectionHandling++;
              }
              if (m.talkListenRatio?.sales_person_talk_percentage != null) {
                totalTalkListenRatio += m.talkListenRatio.sales_person_talk_percentage;
                countTalkListenRatio++;
              }
            });

            // Calculate averages for each metric separately
            metrics = {
              clientSentiment: countDealSentiment > 0 ? Math.round(totalDealSentiment / countDealSentiment) : 75,
              callQualityScore: countCallQuality > 0 ? Math.round(totalCallQuality / countCallQuality) : 75,
              sopAdherence: countSopAdherence > 0 ? Math.round(totalSopAdherence / countSopAdherence) : 75,
              sopDeviationPercentage: countSopDeviationPercentage > 0 ? Math.round(totalSopDeviationPercentage / countSopDeviationPercentage) : 75,
              objectionHandlingScore: countObjectionHandling > 0 ? Math.round(totalObjectionHandling / countObjectionHandling) : 75,
              talkListenRatio: countTalkListenRatio > 0 ? Math.round(totalTalkListenRatio / countTalkListenRatio) : 55,
              buyer_interest_level_text: firstDealSentiment?.buyer_interest_level_text || firstDealSentiment?.buyer_interest_level || undefined,
              overall_deal_sentiment_score: firstDealSentiment?.overall_deal_sentiment_score || undefined
            };
          } 
        } 
        // If we don't have real data from API, try to get sales person metrics
        if (!metrics && salesExecutiveId) {
          try {
            const salesMetrics = await metricsService.getSalespersonMetricsSummary(salesExecutiveId);
            metrics = {
              clientSentiment: 75, // This comes from conversation, not salesperson
              callQualityScore: 75, // This comes from conversation, not salesperson  
              sopAdherence: salesMetrics.sop_adherence_percentage ?? 75,
              sopDeviationPercentage: salesMetrics.sop_adherence_percentage ?? 75,
              objectionHandlingScore: salesMetrics.objection_handling_score ?? 75,
              talkListenRatio: salesMetrics.average_talk_percentage ?? 55,
              buyer_interest_level_text: undefined,
              overall_deal_sentiment_score: undefined
            };
          } catch (error) {
            console.warn(`[MeetingCard ${meeting_id}] Failed to fetch salesperson metrics:`, error);
          }
        }

        // If we still don't have metrics, use calculated data from static file as last resort
        // Include meeting_id to ensure unique scores per meeting
        if (!metrics) {
          metrics = calculateSentimentScore(project, client, salesRep, meeting_id || String(id));
        }

        setMetrics(metrics);
      } catch (error) {
        console.error(`[MeetingCard ${meeting_id}] Error in fetchMetrics:`, error);
        // Fall back to calculated data only if all API calls fail
        // Include meeting_id to ensure unique scores per meeting
        setMetrics(calculateSentimentScore(project, client, salesRep, meeting_id || String(id)));
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchMetrics();
  }, [hasBeenVisible, conversation_id, salesExecutiveId, meeting_id, project, client, salesRep, id]);

  // Once the card is visible, fetch its recording and read the audio length to
  // display on the card. Loads only metadata (not the whole file). Cards with no
  // recording simply leave audioDuration null.
  useEffect(() => {
    if (!hasBeenVisible || !conversation_id) return;

    const convId = Array.isArray(conversation_id)
      ? conversation_id.find(cid => cid && typeof cid === 'string' && cid.trim() !== '')
      : (typeof conversation_id === 'string' && conversation_id.trim() !== '' ? conversation_id : null);
    if (!convId) return;

    const businessId = getUserBusinessId();
    if (!businessId) return;

    let cancelled = false;
    let audioEl: HTMLAudioElement | null = null;

    (async () => {
      try {
        const url = await conversationService.getConversationRecording(businessId, convId);
        if (cancelled) return;
        const audio = new Audio();
        audio.preload = 'metadata';
        audio.onloadedmetadata = () => {
          if (!cancelled && isFinite(audio.duration)) {
            setAudioDuration(audio.duration);
          }
        };
        audio.src = url;
        audioEl = audio;
      } catch {
        // No recording available (or fetch failed) — leave audioDuration unset.
      }
    })();

    return () => {
      cancelled = true;
      if (audioEl) {
        audioEl.onloadedmetadata = null;
        audioEl.src = '';
        audioEl = null;
      }
    };
  }, [hasBeenVisible, conversation_id]);

  // Use fetched metrics or fallback to calculated data while loading
  // const displayMetrics = metrics || calculateSentimentScore(project, client, salesRep);
  
  const formatToOneDecimal = (value?: number | null) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return 0;
    }
    return Math.round(value * 10) / 10;
  };

  const formatToOneDecimalString = (value?: number | null) =>
    formatToOneDecimal(value).toFixed(0);

  // Get badge colors based on buyer interest level
  const getInterestLevelColors = (interestLevel?: string) => {
    if (!interestLevel) return { bg: 'gray.100', color: 'gray.700' };
    const level = interestLevel.toLowerCase();
    if (level === 'high') return { bg: 'green.100', color: 'green.700' };
    if (level === 'medium') return { bg: 'orange.100', color: 'orange.700' };
    if (level === 'low') return { bg: 'red.100', color: 'red.700' };
    return { bg: 'gray.100', color: 'gray.700' };
  };

  // Include meeting_id/id in fallback to ensure uniqueness per meeting
  const displayMetrics = metrics || calculateSentimentScore(project, client, salesRep, meeting_id || String(id));

  // Helper function to check if pendingActions is the object type with count and items
  const isPendingActionsObject = (actions: any): actions is { count: number; items: Array<PendingActionItem> } => {
    return actions && typeof actions === 'object' && !Array.isArray(actions) && 'count' in actions && 'items' in actions;
  };

      const { 
        meetings: contextMeetings, 
      } = useMeetingContext();
  

  const onClientModalOpen = async () => {
    onClientModalOpenOrig();
    if (!clientDetails && clientId) {
      setIsLoadingClient(true);
      setClientError(null);
      
      try {
        // Fetch client details
        const data = await clientService.getClientById(clientId);
        
        // Create filtered meetings based on context meetings for this client
        const clientMeetings = contextMeetings.filter(c => c.clientId === clientId);
        const meetingCount = clientMeetings.length;
        
        const meet = clientMeetings.map((meeting: any) => ({
          ...meeting,
          meetingCount: meetingCount
        }));
        setFilteredMeetings(meet);
        
        
        if (data && typeof (data as any).error === 'undefined') {
          setClientDetails(data as ClientData);
        } else {
          throw new Error((data as any).message || 'Failed to load client details due to an unknown API error.');
        }
      } catch (err: any) {
        setClientError(err.message || "Failed to load client details.");
        setClientDetails(null);
      } finally {
        setIsLoadingClient(false);
      }
    }
  };

  const onClientModalClose = () => {
    onClientModalCloseOrig();
  };

  const onRepModalOpen = async () => {
    onRepModalOpenOrig();
    if (!salesExecutiveId) {
      return;
    }

    if (!salesPersonDetails) {
      setIsLoadingSalesPerson(true);
      setSalesPersonError(null);
      try {
        const data = await salesPersonService.getSalesPersonDetails(salesExecutiveId);
        if (data && typeof (data as any).error === 'undefined') {
          setSalesPersonDetails(data as SalesPersonDetails);
        } else {
          throw new Error((data as any).message || 'Failed to load sales person details due to an unknown API error.');
        }
      } catch (err: any) {
        setSalesPersonError(err.message || "Failed to load sales person details.");
        setSalesPersonDetails(null);
      } finally {
        setIsLoadingSalesPerson(false);
      }
    }

    try {
      const summary = await metricsService.getSalespersonMetricsSummary(salesExecutiveId);
      const fallbackMetrics = metrics ?? calculateSentimentScore(project, client, salesRep);

      setMetrics({
        ...fallbackMetrics,
        sopAdherence: summary.sop_adherence_percentage ?? fallbackMetrics.sopAdherence,
        sopDeviationPercentage: summary.sop_adherence_percentage ?? fallbackMetrics.sopDeviationPercentage,
        objectionHandlingScore: summary.objection_handling_score ?? fallbackMetrics.objectionHandlingScore,
        talkListenRatio: summary.average_talk_percentage ?? fallbackMetrics.talkListenRatio
      });
    } catch (error) {
      console.warn(`[MeetingCard ${meeting_id}] Failed to fetch salesperson metrics summary:`, error);
    }
  };
  
  
  const onRepModalClose = () => {
    onRepModalCloseOrig();
  };

  const handleViewSummary = () => {
    const encodedMeetingId = encodeURIComponent(meeting_id);
    // Pass conversation_id as query parameter
    // Note: conversation_id is already a single string here (not array) because
    // MeetingContent expands array conversation_ids into separate cards
    const conversationIdToPass = Array.isArray(conversation_id) 
      ? (conversation_id.length > 0 ? conversation_id[0] : null)
      : conversation_id;
    
    const queryParam = conversationIdToPass 
      ? `?conversation_id=${encodeURIComponent(conversationIdToPass)}`
      : '';
    
    navigate(`/meeting-details/${encodedMeetingId}${queryParam}`);
  };

  return (
    <>
      <SlideFade 
        in={true} 
        offsetY="20px" 
        transition={{ enter: { duration: 0.5, delay } }}
      >
        <Box
          ref={cardRef}
          as="article"
          position="relative"
          w="full"
          maxW="560px"
          mb={4}
          transition="all 0.3s ease"
          _hover={{
            transform: "scale(1.02)",
            boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.15)",
          }}
        >
          <Flex 
            overflow="hidden" 
            bg="white" 
            borderRadius="lg" 
            boxShadow="sm"
            border="1px solid"
            borderColor="gray.200"
            position="relative"
            transition="all 0.3s ease"
            _hover={{
              borderColor: "#805ad6",
            }}
          >
            {/* Left section */}
            <Flex 
              direction="column" 
              flex="1" 
              p={6}
            >
              <Heading 
                as="h3" 
                fontSize="base" 
                fontWeight="medium" 
                color="blue.900"
                mb={1}
              >
                {name}
              </Heading>
              {/* Show conversation ID if it's a string (not array) */}
              {/* {conversation_id && typeof conversation_id === 'string' && (
                <Text fontSize="xs" color="blue.600" fontWeight="semibold" mb={1} title={conversation_id}>
                  ID: {conversation_id}
                </Text>
              )} */}
              <Text fontSize="xs" color="green.500" fontWeight="medium" mb={1}>
                {date ? formatDate(date) : 'N/A'}
              </Text>
              {/* <Text fontSize="sm" color="gray.800" mb={1}>
                {project}
              </Text> */}
              <Text fontSize="xs" color="gray.500" mb={0.5}>
                Client: {client}
              </Text>
              <Text fontSize="xs" color="gray.500" mb={3}>
                Sale's Rep: {salesRep}
              </Text>
              
              <HStack spacing={2} mt="auto">
                <Button 
                  size="sm" 
                  variant="outline" 
                  fontSize="xs" 
                  height="auto"
                  py={2}
                  px={4}
                  borderRadius="md"
                  borderColor="gray.800"
                  color="gray.800"
                  onClick={onClientModalOpen}
                  isDisabled={!clientId}
                >
                  View Client
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  fontSize="xs" 
                  height="auto"
                  py={2}
                  px={4}
                  borderRadius="md"
                  borderColor="gray.800"
                  color="gray.800"
                  onClick={()=>onRepModalOpen()}
                  isDisabled={!salesExecutiveId}
                >
                  View Rep.
                </Button>
              </HStack>
            </Flex>

            {/* Vertical divider */}
            {/* <Box
              position="absolute"
              left="254px"
              top="0"
              bottom="0"
              width="1px"
              bg="gray.100"
              zIndex="1"
            /> */}

            {/* Right section */}
            <Flex 
              direction="column" 
              justify="space-between" 
              p={6} 
              bg="white"
              minW="200px"
            >
              {audioDuration != null && (
              <Badge
                alignSelf="flex-start"
                bg="blue.100"
                color="gray.700"
                fontSize="xs"
                py={1}
                px={2}
                borderRadius="sm"
                fontWeight="normal"
              >
                {/* ({duration || 'N/A'}) */}
                {formatAudioDuration(audioDuration)}
              </Badge>
              )}
              {/* {audioDuration != null && (
                <Badge
                  alignSelf="flex-start"
                  bg="purple.100"
                  color="purple.700"
                  fontSize="xs"
                  py={1}
                  px={2}
                  mt={1}
                  borderRadius="sm"
                  fontWeight="normal"
                >
                  Audio: {formatAudioTime(audioDuration)}
                </Badge>
              )} */}

              <Spacer />

              <VStack spacing={2} align="stretch" mt={5}>
              <Flex 
                  alignItems="center" 
                  fontSize="xs" 
                  color="gray.500"
                >
                  <Text mr={2}>Client Sentiment:</Text>
                  {metricsLoading ? (
                    <Spinner size="sm" color="blue.500" />
                  ) : (
                    <HStack spacing={1}>
                      <CircularProgress 
                        value={formatToOneDecimal(displayMetrics.clientSentiment)} 
                        color={displayMetrics.clientSentiment < 70 ? "red.500" : "green.500"}
                        size="30px"
                        thickness="10px"
                      >
                        <CircularProgressLabel fontSize="8px" fontWeight="bold">
                          {formatToOneDecimalString(displayMetrics.clientSentiment)}%
                        </CircularProgressLabel>
                      </CircularProgress>
                      {conversation_id && metrics && metrics.buyer_interest_level_text ? (
                        (() => {
                          const { bg, color } = getInterestLevelColors(metrics.buyer_interest_level_text);
                          return (
                            <Badge 
                              bg={bg} 
                              color={color} 
                              fontSize="6px" 
                              px={1} 
                              py={0.5} 
                              borderRadius="sm"
                            >
                              {metrics.buyer_interest_level_text}
                            </Badge>
                          );
                        })()
                      ) : (
                        <Badge bg="orange.100" color="orange.700" fontSize="6px" px={1} py={0.5} borderRadius="sm">
                          Calc
                        </Badge>
                      )}
                    </HStack>
                  )}
                </Flex>
                {metrics?.overall_deal_sentiment_score != null && (
                  <Flex 
                    alignItems="center" 
                    fontSize="xs" 
                    color="gray.500"
                    mt={2}
                  >
                    <Text mr={2}>Deal Sentiment:</Text>
                    {metricsLoading ? (
                      <Spinner size="sm" color="blue.500" />
                    ) : (
                      <HStack spacing={1}>
                        <CircularProgress 
                          value={formatToOneDecimal(metrics.overall_deal_sentiment_score)} 
                          color={metrics.overall_deal_sentiment_score < 70 ? "red.500" : "green.500"}
                          size="30px"
                          thickness="10px"
                        >
                          <CircularProgressLabel fontSize="8px" fontWeight="bold">
                            {formatToOneDecimalString(metrics.overall_deal_sentiment_score)}%
                          </CircularProgressLabel>
                        </CircularProgress>
                        {/* {conversation_id && metrics ? (
                          <Badge bg="green.100" color="green.700" fontSize="6px" px={1} py={0.5} borderRadius="sm">
                            API
                          </Badge>
                        ) : (
                          <Badge bg="orange.100" color="orange.700" fontSize="6px" px={1} py={0.5} borderRadius="sm">
                            Calc
                          </Badge>
                        )} */}
                      </HStack>
                    )}
                  </Flex>
                )}
              </VStack>

              {/* <VStack spacing={1} align="stretch" mt={4}>
                {pendingActions && isPendingActionsObject(pendingActions) && pendingActions.count > 0 ? (
                  <>
                    <Flex 
                      alignItems="center" 
                      fontSize="xs" 
                      color="gray.500"
                      mb={1}
                    >
                      <Text mr={2}>Pending Actions:</Text>
                      <Badge 
                        bg="red.50" 
                        color="red.700" 
                        px={2} 
                        py={1} 
                        borderRadius="sm"
                        fontSize="xs"
                        fontWeight="semibold"
                      >
                        {pendingActions.count}
                      </Badge>
                    </Flex>
                    
                    {pendingActions.items.map((action: PendingActionItem, index: number) => (
                      <PendingAction key={action.id || index} text={action.description} />
                    ))}
                  </>
                ) : (
                  <Text fontSize="xs" color="gray.400">No pending actions.</Text>
                )}
              </VStack> */}

              <Button 
                mt={4}
                bg="gray.800" 
                color="white" 
                size="sm" 
                fontSize="xs" 
                height="auto"
                py={2}
                px={4}
                borderRadius="md"
                _hover={{ bg: "gray.700" }}
                onClick={handleViewSummary}
              >
                View Summary
              </Button>
            </Flex>
          </Flex>
        </Box>
      </SlideFade>

      {/* Client Details Modal */}
      <InfoModal 
        isOpen={isClientModalOpen} 
        onClose={onClientModalClose}
        headerText="Client Details"
      >
        {isLoadingClient && <Spinner />}
        {clientError && <Alert status="error"><AlertIcon />{clientError}</Alert>}
        {!isLoadingClient && !clientError && clientDetails && 
          (() => {
            const clientCardData = {
              id: clientDetails.id,
              name: clientDetails.name,
              status: clientDetails.status,
              email: clientDetails.email,
              phone: clientDetails.phone || 'N/A',
              type: clientDetails.type,
              projectsShown: clientDetails.projectsShown,
              projectsList: clientDetails.projectsList || "N/A",
              pendingAction: clientDetails.pendingAction || "N/A",
              lastMeetingDate: clientDetails.lastMeetingDate || "N/A",
              meetingCount: clientDetails.meetingCount,
              isPendingActionHighlighted: clientDetails.isPendingActionHighlighted || false,
            };
            return <ClientCard client={clientDetails} />;
          })()
        }
        {!isLoadingClient && !clientError && !clientDetails && 
          <Text>Client details could not be loaded or are not available.</Text>
        }
      </InfoModal>

      {/* Sales Rep Modal */}
      <InfoModal 
        isOpen={isRepModalOpen} 
        onClose={onRepModalClose}
        headerText="Sales Representative Details"
      >
        {isLoadingSalesPerson && <Spinner />}
        {salesPersonError && <Alert status="error"><AlertIcon />{salesPersonError}</Alert>}
        {!isLoadingSalesPerson && !salesPersonError && salesPersonDetails && 
          (() => {
            const salesExecutiveCardData = {
              id: salesPersonDetails.details?.user_id || 0,
              name: salesPersonDetails.details?.full_name || "N/A",
              email: salesPersonDetails.details?.email || "N/A",
              sopAdherence: displayMetrics.sopAdherence,
              objectionHandling: displayMetrics.objectionHandlingScore,
              talkToListenRatio: displayMetrics.talkListenRatio,
              projects: {
                count: String(salesPersonDetails.details?.project_ids?.length || 0),
                names: (salesPersonDetails.details?.project_ids || []).join(', ') || "N/A",
              },
              pendingActions: "0", // Placeholder: Data not available in API response
              totalMeetings: String(salesPersonDetails.stats?.totalMeetings || 0),
              activeClients: String(salesPersonDetails.stats?.activeClientCount || 0),
            };            
            return <SalesExecutiveCard executive={salesExecutiveCardData} />;
          })()
        }
        {!isLoadingSalesPerson && !salesPersonError && !salesPersonDetails && 
          <Text>Sales representative details could not be loaded or are not available.</Text>
        }
      </InfoModal>
    </>
  );
};

export default MeetingCard; 